import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';

import type {
  CreateReportTemplateRequest,
  SheetConfiguration,
  TemplateUploadResponse,
} from './hooks/useReportTemplates';
import { useReportTemplates } from './hooks/useReportTemplates';
import ReportTemplateForm from './ReportTemplateForm';
import { REPORT_TEMPLATES_TEXTS } from './texts';
import type { FormValues, SheetsValidationState } from './types';
import { isConfiguredSheet } from './types';
import type { UploadResult } from './UploadReportFileStep';
import UploadReportFileStep from './UploadReportFileStep';

interface CreateTemplateDialogProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({ onClose, onSuccess }) => {
  const { createTemplate, isSubmitting } = useReportTemplates();

  const [uploadedData, setUploadedData] = useState<TemplateUploadResponse | null>(null);
  const [sheetsValidationState, setSheetsValidationState] = useState<SheetsValidationState>({});
  const [initialFormValues, setInitialFormValues] = useState<FormValues>({
    templateName: '',
    sheets: [],
  });

  const handleUploadComplete = (data: UploadResult) => {
    setUploadedData(data.uploadResponse);
    setSheetsValidationState(data.sheetsValidationState);
    setInitialFormValues(data.initialFormValues);
  };

  const handleDeleteFile = () => {
    setUploadedData(null);
    setSheetsValidationState({});
    setInitialFormValues({ templateName: '', sheets: [] });
  };

  const handleFormSubmit = async (values: FormValues) => {
    if (!uploadedData) return;

    // Filter sheets that have been configured using type guard
    const configuredSheets: SheetConfiguration[] = values.sheets.filter(isConfiguredSheet).map((sheet) => ({
      sheet_name: sheet.sheet_name,
      display_name: sheet.display_name,
      metabase_question_id: sheet.metabase_question_id,
    }));

    const request: CreateReportTemplateRequest = {
      name: values.templateName,
      gcs_path: uploadedData.gcs_path,
      sheets: configuredSheets,
    };

    const result = await createTemplate(request);
    if (result) {
      onSuccess?.();
      onClose();
    }
  };

  return (
    <CardDialog
      isDialog
      title={REPORT_TEMPLATES_TEXTS.CREATE_DIALOG_TITLE}
      onClose={onClose}
      preventClose={isSubmitting}
      maxWidth="sm"
      fullWidth
    >
      <div className="flex flex-col gap-16">
        {!uploadedData ? <UploadReportFileStep onUploadComplete={handleUploadComplete} onCancel={onClose} /> : null}

        {uploadedData ? (
          <ReportTemplateForm
            mode="create"
            initialValues={initialFormValues}
            uploadedData={uploadedData}
            initialSheetsValidationState={sheetsValidationState}
            onSubmit={handleFormSubmit}
            onCancel={onClose}
            onDeleteFile={handleDeleteFile}
            isSubmitting={isSubmitting}
          />
        ) : null}
      </div>
    </CardDialog>
  );
};

export default CreateTemplateDialog;
